import { useMemo, FC } from 'react'
import { makeStyles, Theme } from '@material-ui/core/styles'

import Typography from '@material-ui/core/Typography'
import CircularProgress from '@material-ui/core/CircularProgress'
import Button from '@material-ui/core/Button'

import ChatItem from './chat-item'

import { Conversation } from '../../models/Conversation.model'
import useConversationsStore from '@/stores/useConversationsStore'
import { useConversationsFetcher } from '../../hooks/useConversationsFetcher'

import { getCategory, CATEGORIES, CategoryKey } from '@/utils/conversation-utils'

const useStyles = makeStyles((theme: Theme) => ({
  scrollableContainer: {
    position: 'relative',
    height: '100%',
    overflow: 'hidden',
    '&:hover $scrollContent::-webkit-scrollbar-thumb': {
      background: 'rgba(158,158,158,0.65)',
    },
  },
  scrollContent: {
    height: '100%',
    overflowY: 'auto',
    paddingRight: 0,
    '&::-webkit-scrollbar': { width: '6px' },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(158,158,158,0.25)',
      borderRadius: '4px',
      transition: 'background-color 0.4s ease',
    },
    '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(158,158,158,0.75)' },
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(158,158,158,0.25) transparent',
    transition: 'scrollbar-color 0.4s ease',
    '&:hover': {
      scrollbarColor: 'rgba(158,158,158,0.75) transparent',
    },
    '$scrollableContainer:hover &': {
      scrollbarColor: 'rgba(158,158,158,0.65) transparent',
    },
  },
  categoryTitle: {
    padding: theme.spacing(0.8, 1.5),
    fontWeight: 'bold',
    fontSize: '1rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
    textTransform: 'capitalize',
  },
  centeredMessage: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%', // Ocupa toda la altura disponible del ChatList
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  errorMessageText: {
    color: theme.palette.error.main,
    marginBottom: theme.spacing(1),
  },
}))

const categoryOrder: CategoryKey[] = [
  CATEGORIES.PINNED,
  CATEGORIES.TODAY,
  CATEGORIES.YESTERDAY,
  CATEGORIES.LAST_7_DAYS,
  CATEGORIES.LAST_MONTH,
  CATEGORIES.OLDER,
]

const ChatList: FC = () => {
  const classes = useStyles()

  const rawConversations = useConversationsStore((state) => state.conversations) as Conversation[]
  const searchTerm = useConversationsStore((state) => state.searchTerm)
  const selectPinnedConversations = useConversationsStore((state) => state.selectPinnedConversations)
  const selectRegularConversations = useConversationsStore((state) => state.selectRegularConversations)
  const { isLoading, error, fetchConversationsList } = useConversationsFetcher()

  const categorizedConversations: Record<CategoryKey, Conversation[]> = useMemo(() => {
    if (!rawConversations || rawConversations.length === 0) {
      return categoryOrder.reduce((acc, key) => {
        acc[key] = []
        return acc
      }, {} as Record<CategoryKey, Conversation[]>)
    }

    // Get pinned and regular conversations from store selectors
    const pinnedConversations = selectPinnedConversations() // Already sorted alphabetically
    const regularConversations = selectRegularConversations()

    // Filter conversations by search term if provided
    const filteredPinnedConversations =
      searchTerm && searchTerm.trim() !== ''
        ? pinnedConversations.filter((conv) =>
            conv.title.toLowerCase().includes(searchTerm.toLowerCase().trim())
          )
        : pinnedConversations

    const filteredRegularConversations =
      searchTerm && searchTerm.trim() !== ''
        ? regularConversations.filter((conv) =>
            conv.title.toLowerCase().includes(searchTerm.toLowerCase().trim())
          )
        : regularConversations

    // Initialize categories
    const categories = categoryOrder.reduce((acc, key) => {
      acc[key] = []
      return acc
    }, {} as Record<CategoryKey, Conversation[]>)

    // Add pinned conversations (already sorted alphabetically by the store selector)
    categories[CATEGORIES.PINNED] = filteredPinnedConversations

    // Categorize regular conversations by date
    filteredRegularConversations.forEach((conv) => {
      const cat = getCategory(conv.createdAt)
      categories[cat].push(conv)
    })

    // Sort regular conversation categories by date (newest first)
    categoryOrder.forEach((key) => {
      if (key !== CATEGORIES.PINNED) {
        categories[key].sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )
      }
    })

    return categories
  }, [rawConversations, searchTerm, selectPinnedConversations, selectRegularConversations])

  if (isLoading && rawConversations.length === 0) {
    return (
      <div className={classes.centeredMessage}>
        <Typography variant="h6">Loading conversations...</Typography>
        <CircularProgress />
      </div>
    )
  }

  if (error && rawConversations.length === 0) {
    // Si hay un error y no hay NADA en el store (ni siquiera de sessionStorage)
    return (
      <div className={classes.centeredMessage}>
        <Typography variant="h6" className={classes.errorMessageText}>
          Error Loading Conversations
        </Typography>
        <Typography variant="body2" className={classes.errorMessageText} paragraph>
          {error.message}
        </Typography>
        <Button variant="outlined" onClick={() => fetchConversationsList()}>
          Retry
        </Button>
      </div>
    )
  }

  if (!isLoading && !error && rawConversations.length === 0) {
    return (
      <div className={classes.centeredMessage}>
        <Typography variant="body1">No conversations found.</Typography>
      </div>
    )
  }

  return (
    <div className={classes.scrollableContainer}>
      <div className={classes.scrollContent}>
        {categoryOrder.map((categoryKey) => {
          const conversationsInCategory = categorizedConversations[categoryKey]
          return (
            conversationsInCategory &&
            conversationsInCategory.length > 0 && (
              <div key={categoryKey}>
                <Typography variant="subtitle2" className={classes.categoryTitle}>
                  {categoryKey}
                </Typography>
                {conversationsInCategory.map(({ id, title }: Conversation) => (
                  <ChatItem key={id} chatId={id} chatName={title} />
                ))}
              </div>
            )
          )
        })}
      </div>
    </div>
  )
}

export default ChatList
